import { useEffect, useState, useCallback } from 'react';
import { apiRequest } from '../Api';
import { API_ENDPOINTS } from '../constants/ApiEndpoints';

/**
 * Custom hook for fetching detailed case information and analysis history
 * Retrieves a single case with all associated analyses/diagnoses
 *
 * @param {number} caseId - The ID of the case to fetch
 * @returns {Object} { case, analyses, isLoading, error, refetch, updateCaseStatus }
 */
export const useCaseDetail = (caseId) => {
  const [caseData, setCaseData] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [lmbaData, setLmbaData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCaseDetail = useCallback(async () => {
    if (!caseId) {
      setError(new Error('Case ID is required'));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch case details
      const caseResponse = await apiRequest(`/api/Case/${caseId}`);
      const caseDetails = caseResponse.data || caseResponse;

      // Fetch patient details to enrich case data with demographics and vitals
      let enrichedCaseData = { ...caseDetails };
      const patientId = caseDetails?.patientId || caseDetails?.PatientId;

      if (patientId) {
        try {
          const patientResponse = await apiRequest(`/api/patient/${patientId}`);
          const patientData = patientResponse.data || patientResponse;
          // Merge patient demographics and vitals into case data
          // Some backend versions return vitals as a nested object, others as flat properties
          const vitals = patientData.vitalSigns || patientData.VitalSigns || {
            bloodPressure: patientData.bloodPressure || patientData.BloodPressure,
            heartRate: patientData.heartRate || patientData.HeartRate,
            temperature: patientData.temperature || patientData.Temperature,
            respiratoryRate: patientData.respiratoryRate || patientData.RespiratoryRate,
            oxygenSaturation: patientData.oxygenSaturation || patientData.OxygenSaturation,
          };

          enrichedCaseData = {
            ...enrichedCaseData,
            age: patientData.age || patientData.Age,
            gender: patientData.gender || patientData.Gender,
            bmi: patientData.bmi || patientData.BMI,
            vitals,
            allergies: patientData.allergies || patientData.Allergies,
            medications: patientData.medications || patientData.Medications,
            pastConditions: patientData.pastConditions || patientData.PastConditions,
            familyHistory: patientData.familyHistory || patientData.FamilyHistory,
            socialHistory: patientData.socialHistory || patientData.SocialHistory,
          };
        } catch (patientErr) {
          console.log('Could not fetch patient demographics for patient:', patientId);
        }
      }

      setCaseData(enrichedCaseData);

      // Fetch analyses/diagnoses for this case
      try {
        const analysisResponse = await apiRequest(`/api/Analysis/results/by-case/${caseId}`);
        const analysesData = Array.isArray(analysisResponse)
          ? analysisResponse
          : analysisResponse.data || [];
        setAnalyses(analysesData);
      } catch (analysisErr) {
        // If analysis fetch fails, continue with case data
        // (analyses might not exist yet for new cases)
        console.log('No analyses found for case:', caseId);
        setAnalyses([]);
      }

      // Fetch LMBA history if patient ID is available
      if (patientId) {
        try {
          const lmbaResponse = await apiRequest(`/api/patient/${patientId}/lmba-history`);
          const lmbaHistory = Array.isArray(lmbaResponse)
            ? lmbaResponse
            : lmbaResponse.data || [];
          setLmbaData(lmbaHistory);
        } catch (lmbaErr) {
          // If LMBA fetch fails, continue without it
          console.log('No LMBA data found for patient:', patientId);
          setLmbaData([]);
        }
      }
    } catch (err) {
      setError(err);
      setCaseData(null);
      setAnalyses([]);
      setLmbaData([]);
    } finally {
      setIsLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchCaseDetail();
  }, [fetchCaseDetail]);

  const updateCaseStatus = useCallback(
    async (newStatus) => {
      if (!caseData) return;

      try {
        const response = await apiRequest(`/api/Case/${caseId}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus }),
        });

        const updatedCase = response.data || response;
        setCaseData(updatedCase);
        return updatedCase;
      } catch (err) {
        setError(err);
        throw err;
      }
    },
    [caseData, caseId]
  );

  const setRunningTheory = useCallback(
    async (diagnosisName) => {
      if (!caseId) return;
      const response = await apiRequest(API_ENDPOINTS.CASE.SET_RUNNING_THEORY(caseId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ diagnosisName }),
      });
      const updated = response.data || response;
      setCaseData(updated);
      return updated;
    },
    [caseId]
  );

  const confirmDiagnosis = useCallback(
    async () => {
      if (!caseId) return;
      const response = await apiRequest(API_ENDPOINTS.CASE.CONFIRM_DIAGNOSIS(caseId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
      });
      const updated = response.data || response;
      setCaseData(updated);
      return updated;
    },
    [caseId]
  );

  return {
    case: caseData,
    analyses,
    lmbaData,
    isLoading,
    error,
    refetch: fetchCaseDetail,
    updateCaseStatus,
    setRunningTheory,
    confirmDiagnosis,
  };
};

export default useCaseDetail;
